import React from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { PIE_PALETTE } from '../operations/operationsUi';

export const DATA_START_DATE = new Date('2026-07-06T00:00:00.000Z');

export const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '12px',
    fontSize: '12px',
  },
  labelStyle: { color: '#9ca3af' },
  itemStyle: { color: '#fff' },
};

export function formatCurrency(value: number, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
}

export function formatCompact(value: number) {
  const abs = Math.abs(value || 0);
  if (abs >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `₹${(value / 1000).toFixed(1)}k`;
  return `₹${(value || 0).toFixed(0)}`;
}

export function formatDateShort(value: string | Date) {
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function paletteColor(index: number) {
  return PIE_PALETTE[index % PIE_PALETTE.length];
}

export function parseGrowth(growth?: string): { direction: 'up' | 'down' | 'flat' | 'na'; label: string } {
  if (!growth || growth === 'N/A') return { direction: 'na', label: 'N/A' };
  const numeric = Number(String(growth).replace(/[+%]/g, ''));
  if (!Number.isFinite(numeric) || Math.abs(numeric) < 0.05) return { direction: 'flat', label: growth };
  if (String(growth).startsWith('-') || numeric < 0) return { direction: 'down', label: growth };
  return { direction: 'up', label: growth };
}

export function GrowthBadge({ growth, valid = true }: { growth: string; valid?: boolean }) {
  if (!valid) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        N/A
      </span>
    );
  }
  const parsed = parseGrowth(growth);
  if (parsed.direction === 'na' || parsed.direction === 'flat') {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        {parsed.label}
      </span>
    );
  }
  const up = parsed.direction === 'up';
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        up
          ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
      }`}
    >
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {parsed.label}
    </span>
  );
}

export function ShareBar({ share, color }: { share: number; color: string }) {
  return (
    <div className="flex min-w-[88px] items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, share))}%`, backgroundColor: color }} />
      </div>
      <span className="w-10 text-right text-xs font-semibold tabular-nums text-gray-600 dark:text-gray-300">
        {share.toFixed(1)}%
      </span>
    </div>
  );
}

export function SortHeader({
  label,
  field,
  sortField,
  sortOrder,
  onSort,
  align = 'left',
}: {
  label: string;
  field: string;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const active = sortField === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 ${
        align === 'right' ? 'ml-auto' : align === 'center' ? 'mx-auto' : ''
      }`}
    >
      {label}
      <span className={active ? 'text-primary-600' : 'text-gray-300 dark:text-gray-600'}>
        {active ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

export const thClass = 'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
export const tdClass = 'px-4 py-3 text-gray-800 dark:text-gray-200';
